import React, { useState } from "react";
import { Link, NavLink, useNavigate, Outlet } from "react-router-dom";
import {
  LayoutDashboard,
  FileSpreadsheet,
  UploadCloud,
  Users,
  Tag,
  Boxes,
  LogOut,
  ShieldCheck,
  Activity,
  ListTree,
  FileDown,
  KeyRound,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { PERM, hasPerm } from "@/lib/permissions";
import { ROLE_LABEL } from "@/lib/roles";
import ChangePasswordModal from "@/components/users/ChangePasswordModal";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_prep-reviewer-login/artifacts/dzc1abq3_PrepHacker_Featured_Logo_Final.png";

function navFor(user) {
  const items = [{ to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" }];
  if (hasPerm(user, PERM.QUESTIONS_UPLOAD)) {
    items.push({ to: "/upload", icon: UploadCloud, label: "Upload" });
  }
  if (hasPerm(user, PERM.QUESTIONS_VIEW) && user?.role !== "sme_reviewer") {
    items.push({ to: "/questions", icon: FileSpreadsheet, label: "Raw Questions" });
  }
  // Batches: everyone except plain reviewers gets the full list; reviewers
  // see their own batches via the same route (server-filtered).
  items.push({
    to: "/batches",
    icon: Boxes,
    label: user?.role === "sme_reviewer" ? "My Batches" : "Batches",
  });
  if (hasPerm(user, PERM.QUESTIONS_QC)) {
    items.push({ to: "/qc", icon: ShieldCheck, label: "Quality Control" });
  }
  if (hasPerm(user, PERM.QUESTIONS_EXPORT)) {
    items.push({ to: "/exports", icon: FileDown, label: "Exports" });
  }
  items.push({ to: "/activity", icon: Activity, label: "Activity Log" });
  if (hasPerm(user, PERM.TAGS_MANAGE)) {
    items.push({ to: "/tags", icon: Tag, label: "Tags" });
    items.push({ to: "/lookups", icon: ListTree, label: "Dropdowns" });
  }
  if (hasPerm(user, PERM.USERS_MANAGE) || hasPerm(user, PERM.PERMISSIONS_GRANT)) {
    items.push({ to: "/users", icon: Users, label: "Users" });
  }
  return items;
}

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const items = navFor(user);
  const [changePwOpen, setChangePwOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[#f7f9fb] flex">
      <aside
        data-testid="sidebar-nav"
        className="w-64 shrink-0 bg-white border-r border-slate-200 flex flex-col"
      >
        <div className="p-5 border-b border-slate-100">
          <Link to="/dashboard" className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-slate-900 flex items-center justify-center overflow-hidden">
              <img src={LOGO_URL} alt="PrepHacker" className="w-full h-full object-contain" />
            </div>
            <div className="leading-tight">
              <div className="text-[15px] font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>
                PrepHacker
              </div>
              <div className="text-[10px] uppercase tracking-wider text-slate-400 font-mono">
                Content Hub
              </div>
            </div>
          </Link>
        </div>

        <nav className="p-3 flex-1 space-y-1">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              data-testid={`nav-${it.to.replace("/", "")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-orange-50 text-orange-600"
                    : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                }`
              }
            >
              <it.icon className="w-4 h-4" />
              <span>{it.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-slate-100">
          <div className="px-3 py-2 rounded-lg bg-slate-50">
            <div className="text-xs text-slate-400 font-mono">SIGNED IN AS</div>
            <div className="text-sm font-semibold text-slate-900 truncate" title={user?.email}>
              {user?.name || user?.email}
            </div>
            <div className="text-[11px] text-orange-600 font-mono uppercase tracking-wider">
              {ROLE_LABEL[user?.role] || user?.role}
            </div>
          </div>
          <button
            data-testid="change-password-btn"
            onClick={() => setChangePwOpen(true)}
            className="mt-2 w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-500 hover:text-slate-900 hover:bg-slate-50 transition"
          >
            <KeyRound className="w-4 h-4" />
            Change password
          </button>
          <button
            data-testid="logout-btn"
            onClick={async () => {
              await logout();
              navigate("/login");
            }}
            className="mt-1 w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-500 hover:text-slate-900 hover:bg-slate-50 transition"
          >
            <LogOut className="w-4 h-4" />
            Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <Outlet />
      </main>
      {changePwOpen && <ChangePasswordModal onClose={() => setChangePwOpen(false)} />}
    </div>
  );
}

export function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: "Outfit" }}>
          {title}
        </h1>
        {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

export function PageContainer({ children }) {
  return <div className="px-8 py-8 max-w-[1400px] mx-auto">{children}</div>;
}
